import {
  UserLocationsDocument as Query,
  useExternalOrderSelectedLocationQuery,
  useUserLocationsQuery,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Select from '@/gf/components/next/Select'
import { Maybe } from '@/types'
import { useMemo, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import { Link } from 'react-router-dom'

export { Query }

const LocationSelector = ({
  value,
  onChange,
}: {
  value: Maybe<string>
  onChange: (newValue: Maybe<string>) => void
}) => {
  const { orgId } = useSession()

  const client = useGqlClient()
  const [search, setSearch] = useState('')
  const debouncedSetSearch = useRef(debounce(setSearch, 400)).current

  const { data, loading } = useUserLocationsQuery({
    variables: { orgId, search: search || null },
    client,
    skip: !orgId,
  })

  const { data: selectedData, loading: loadingSelected } = useExternalOrderSelectedLocationQuery({
    variables: { orgId, id: value! },
    client,
    skip: !value,
  })

  const userLocations = data?.org?.locations ?? []

  const locationName = (l) => `${l.name} ${l.defaultLocation ? `(default location)` : ''}`

  const options = useMemo(() => {
    const userLocationsOptions = userLocations
      .sort((a, _b) => (a.defaultLocation ? -1 : 0)) // default location first
      .map((l) => ({
        label: locationName(l),
        value: l.id,
      }))

    return userLocationsOptions
  }, [userLocations])

  const selectedLocation = selectedData?.shippingLocationsSearch.shippingLocations[0]

  const incompleteLocationAddress =
    selectedLocation &&
    (!selectedLocation.address?.lineOne ||
      !selectedLocation.address?.city ||
      !selectedLocation.address?.state ||
      !selectedLocation.address?.postalCode ||
      !selectedLocation.address?.country)

  const selectedOption = useMemo(
    () =>
      value
        ? {
            value: value,
            label: selectedLocation ? locationName(selectedLocation) : null,
          }
        : null,
    [value, selectedLocation]
  )

  return (
    <div className="space-y-2 w-full max-w-xs">
      <Select
        options={options}
        value={selectedOption}
        onInputChange={(input) => debouncedSetSearch(input)}
        onChange={(o) => (o ? onChange(o.value) : undefined)}
        isLoading={loading || loadingSelected}
        className="w-full"
        placeholder="Search locations..."
      />
      {selectedLocation && incompleteLocationAddress && (
        <div className="w-full">
          <Link
            to={`/locations/${selectedLocation.id}?edit=true`}
            className="w-full inline-flex items-center justify-center px-3 py-1.5 rounded-md bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm font-medium hover:bg-yellow-100 transition-colors whitespace-nowrap overflow-hidden text-ellipsis"
          >
            <ExclamationTriangleIcon className="w-4 h-4 mr-1.5 flex-shrink-0 text-yellow-600" />
            <span className="truncate">Incomplete address - click to update</span>
          </Link>
        </div>
      )}
    </div>
  )
}

export default LocationSelector
